import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, change } from 'redux-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import Button from 'react-bootstrap/lib/Button';
import Grid from 'react-bootstrap/lib/Grid';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import FormControl from 'react-bootstrap/lib/FormControl';
import cx from 'classnames';

import DateRange from '../DateRange/DateRange';
import PlaceGeoSuggest from '../../Common/GeoSuggest/PlaceGeoSuggest';
import MobileDateRange from '../MobileDateRange/MobileDateRange';

import { setPersonalizedValues } from '../../../actions/personalized';
import detectMobileBrowsers from '../../../helpers/detectMobileBrowsers';
import { searchFormSuggestSelect, changeSearchForm } from '../../../helpers/GeoSuggest/updateSearchForm';
import history from '../../../core/history';
import messages from '../../../locale/messages';
import { getPopularLocationAdmin } from '../../../lib/graphql';
import { locationuploadDir } from '../../../config';

import searchIcon from '/public/SiteIcons/searchIconHome.svg';

import bt from '../../commonStyle.css';
import s from './DetailSearchForm.css';

const DetailSearchForm = ({
  setPersonalizedValues,
  personalized,
  change,
  settingsData,
  listingFields,
  intl,
  isLoading
}) => {
  const { data: popularLocationData } = useQuery(getPopularLocationAdmin, {
    ssr: false,
  });

  const [mobileDevice, setMobileDevice] = useState(false);
  const [personCapacity, setPersonCapacity] = useState([]);
  const [isLoad, setIsLoad] = useState(false);
  const [smallDevice, setSmallDevice] = useState(false);
  const [verySmallDevice, setVerySmallDevice] = useState(false);
  const [isOneTotalToggle, setIsOneTotalToggle] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const formRef = useRef(null);

  const allFieldsFilled = !!(
    personalized?.location &&
    personalized?.chosen &&
    personalized?.startDate &&
    personalized?.endDate &&
    personalized?.personCapacity
  );
  const showExpanded = activeField || allFieldsFilled;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (formRef.current && !formRef.current.contains(e.target)) {
        setActiveField(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResize = useCallback(() => {
    const isBrowser = typeof window !== 'undefined';
    if (isBrowser) {
      setSmallDevice(window.matchMedia('(max-width: 767px)').matches);
      setVerySmallDevice(window.matchMedia('(max-width: 480px)').matches);
    }
  }, []);

  useEffect(() => {
    setIsLoad(true);

    if (detectMobileBrowsers.isMobile()) {
      setMobileDevice(true);
    }

    if (listingFields) {
      setPersonCapacity(listingFields.personCapacity || []);
    }

    if (personalized?.isOneTotalToggle) {
      setIsOneTotalToggle(personalized.isOneTotalToggle);
    }

    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [handleResize, listingFields, personalized]);

  const handleClick = () => {
    let uri = '/s?';

    if (personalized?.chosen) {
      uri += `&address=${personalized.location}&chosen=${personalized.chosen}`;
    } else if (personalized?.location) {
      uri += `&address=${personalized.location}`;
    }

    if (personalized?.startDate && personalized?.endDate) {
      setPersonalizedValues({ name: 'isOneTotalToggle', value: true });
      change('SearchForm', 'isOneTotalToggle', true);
      uri += `&startdate=${personalized.startDate}&enddate=${personalized.endDate}`;
    }

    if (personalized?.personCapacity && !isNaN(personalized.personCapacity)) {
      uri += `&guests=${personalized.personCapacity}`;
    }

    history.push(encodeURI(uri));
  };

  const handleFieldClick = (field, inputId) => {
    setActiveField(field);
    if (inputId) {
      const el = document.getElementById(inputId);
      if (el) setTimeout(() => el.focus(), 50);
    }
  };

  const renderFormControl = ({ input, label, type, meta: { touched, error }, className }) => {
    const { formatMessage } = intl;
    return (
      <div>
        {touched && error && <span className={s.errorMessage}>{formatMessage(error)}</span>}
        <FormControl {...input} placeholder={label} type={type} className={className} />
      </div>
    );
  };

  const onSuggestSelect = (data, value) => {
    searchFormSuggestSelect({ data, setPersonalizedValues, value, change: (field, val) => change('DetailSearchForm', field, val) });
  };

  const onChange = async (value) => {
    if (value === '') {
      changeSearchForm({ setPersonalizedValues, change });
    }
  };

  const { formatMessage } = intl;

  let rows = [];
  if (personCapacity?.[0]?.startValue) {
    for (let i = personCapacity[0].startValue; i <= personCapacity[0].endValue; i++) {
      rows.push(
        <option value={i} key={i}>
          {i} {i > 1 ? personCapacity[0].otherItemName : personCapacity[0].itemName}
        </option>
      );
    }
  }

  return (
    <Grid fluid>
      <Row>
        <Col xs={12} sm={12} md={12} lg={12} className={s.noPadding}>
          <form>
            <div className={cx(s.searchFormInputs, 'homeSearchForm', 'detailSearchForm')}>
              <div className={cx(s.searchForm, { [s.formHasActive]: activeField })} ref={formRef}>
                <div className={cx(s.table)}>
                  <div className={cx(s.tableRow)}>
                    <div
                      className={cx(
                        s.tableCell, s.location,
                        s.fieldWrapper,
                        { [s.fieldActive]: activeField === 'location', [s.fieldInactive]: activeField && activeField !== 'location' }
                      )}
                      onClick={() => handleFieldClick('location', 'detailedSearch')}
                    >
                      <label className={cx(s.label, s.displayBlock)}>
                        <span><FormattedMessage {...messages.where} /></span>
                      </label>
                      <PlaceGeoSuggest
                        placeholder={formatMessage(messages.homeWhere)}
                        className={cx(s.input, 'widthFull', s.newGeoSuggestInputContainer)}
                        initialValue={personalized?.location}
                        onChange={onChange}
                        onSuggestSelect={onSuggestSelect}
                        id={'detailedSearch'}
                        containerClass='widthFull layoutDetailSearchGeoSuggest'
                        popularLocations={popularLocationData?.getPopularLocationAdmin}
                        locationuploadDir={locationuploadDir}
                      />
                    </div>
                    <div
                      className={cx(
                        s.tableCell, s.dates,
                        s.fieldWrapper,
                        { [s.fieldActive]: activeField === 'dates', [s.fieldInactive]: activeField && activeField !== 'dates' }
                      )}
                      onClick={() => handleFieldClick('dates', null)}
                    >
                      <label className={cx(s.label)}>
                        <span><FormattedMessage {...messages.when} /></span>
                      </label>
                      <span className={cx('homeDate vidFormsearch', s.input)}>
                        {!smallDevice ? (
                          <DateRange formName={'SearchForm'} numberOfMonths={2} isLoading={isLoading} />
                        ) : (
                          <MobileDateRange
                            formName={'SearchForm'}
                            numberOfMonths={1}
                            smallDevice
                            verySmallDevice={verySmallDevice}
                            isLoading={isLoading}
                          />
                        )}
                      </span>
                    </div>
                    <div
                      className={cx(
                        s.tableCell, s.guests, s.mobilePadding,
                        s.fieldWrapper,
                        { [s.fieldActive]: activeField === 'guests', [s.fieldInactive]: activeField && activeField !== 'guests' }
                      )}
                      onClick={() => handleFieldClick('guests', 'detailGuestSelect')}
                    >
                      <label className={cx(s.selectPadding, s.label)}>
                        <span><FormattedMessage {...messages.guest} /></span>
                      </label>
                      <FormControl
                        componentClass="select"
                        id="detailGuestSelect"
                        className={cx(bt.commonControlSelect, 'commonControlSelectRtl')}
                        onChange={(e) =>
                          setPersonalizedValues({ name: 'personCapacity', value: Number(e.target.value) })
                        }
                        defaultValue={personalized.personCapacity}
                        value={personalized.personCapacity}
                      >
                        {rows}
                      </FormControl>
                    </div>
                    <div className={cx(s.search, 'searchRTL')}>
                      <Button className={cx(bt.btnPrimary, s.btnBlock, { [s.btnExpanded]: showExpanded })} onClick={handleClick}>
                        <img src={searchIcon} className={cx(s.searchIconCommon, 'searchIconCommonRTL')} />
                        {showExpanded && <span><FormattedMessage {...messages.search} /></span>}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Col>
      </Row>
    </Grid>
  );
};

DetailSearchForm.propTypes = {
  setPersonalizedValues: PropTypes.func,
  personalized: PropTypes.object,
  change: PropTypes.func,
  settingsData: PropTypes.object,
  listingFields: PropTypes.object,
  intl: PropTypes.object,
  isLoading: PropTypes.bool
};

const mapState = (state) => ({
  personalized: state.personalized,
  settingsData: state.viewListing.settingsData,
  listingFields: state.listingFields.data,
  isOneTotalToggle: state.personalized.isOneTotalToggle
});

const mapDispatch = {
  setPersonalizedValues,
  change
};

export default injectIntl(withStyles(s, bt)(connect(mapState, mapDispatch)(reduxForm({
  form: 'DetailSearchForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(DetailSearchForm))));
